<template>
  <div :class="['cb', message.role === 'user' ? 'cb--user' : 'cb--assistant']">

    <!-- Assistant avatar -->
    <div v-if="message.role === 'assistant'" class="cb__avatar">
      <img v-if="isProta" :src="protaAvatar" class="cb__avatar-img" alt="Prota" />
      <img v-else-if="isProka" :src="prokaAvatar" class="cb__avatar-img" alt="Proka" />
      <cortex-icon v-else :size="16"></cortex-icon>
    </div>

    <div class="cb__body">
      <!-- Agent name label -->
      <div v-if="message.role === 'assistant' && agentDisplayName" class="cb__agent-name">{{ agentDisplayName }}</div>
      <!-- Thinking block (collapsible) -->
      <div v-if="message.thinking" class="cb__thinking">
        <button class="cb__thinking-header" @click="thinkingOpen = !thinkingOpen">
          <span class="cb__thinking-icon" :class="{ 'cb__thinking-icon--spin': isActivelyThinking }">
            <i class="fas fa-brain"></i>
          </span>
          <span class="cb__thinking-label">
            {{ isActivelyThinking ? $t('kortex.thinking') : $t('kortex.thoughtFor') }}
          </span>
          <i class="fas cb__thinking-chevron" :class="thinkingOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </button>
        <div v-if="thinkingOpen" class="cb__thinking-body">
          <pre class="cb__thinking-text">{{ message.thinking }}</pre>
        </div>
      </div>

      <div v-if="message.isError" class="cb__error">
        <i class="fas fa-exclamation-circle"></i>
        {{ message.errorText || $t('kortex.errorMessage') }}
      </div>
      <div v-else-if="renderedContent" class="cb__content" v-html="renderedContent"></div>
      <div v-if="message.tool_calls && message.tool_calls.length > 0" class="cb__tool-calls">
        <tool-call-card
          v-for="(tc, idx) in message.tool_calls"
          :key="tc.id || idx"
          :tool-call="adaptedToolCall(tc)"
        ></tool-call-card>
      </div>
      <div v-if="message.token_usage" class="cb__meta">
        <i class="fas fa-bolt"></i>
        {{ message.token_usage.total_tokens }} {{ $t('kortex.tokens') }}
      </div>
    </div>

  </div>
</template>

<script>
import ToolCallCard from './ToolCallCard'
var protaAvatar = require('@/assets/images/prota-avatar.png')
var prokaAvatar = require('@/assets/images/proka-avatar.png')

export default {
  name: 'MessageBubble',
  components: {
    ToolCallCard
  },
  props: {
    message: {
      type: Object,
      required: true
    },
    isActive: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      protaAvatar: protaAvatar,
      prokaAvatar: prokaAvatar,
      thinkingOpen: false
    }
  },
  computed: {
    isProta: function () {
      return this.message.agent_uuid === 'prota'
    },
    isProka: function () {
      return this.message.agent_uuid === 'proka'
    },
    agentDisplayName: function () {
      if (this.message.agent_name) return this.message.agent_name
      if (this.isProta) return 'Prota'
      if (this.isProka) return 'Proka'
      return ''
    },
    isActivelyThinking: function () {
      // Use the store's isThinking signal for the currently active message —
      // it's set to false the moment the first content_delta arrives, which is
      // more reliable than checking !message.content (which lags by a tick).
      if (this.isActive) {
        return this.$store.state.cortex.isThinking
      }
      return false
    },
    renderedContent: function () {
      var content = this.message.content || ''
      if (!content) return ''
      // Strip legacy [AgentName]: prefix injected by older multi-agent prompt rule
      content = content.replace(/^\[[^\]]+\]:\s*/, '')

      // Extract fenced code blocks before escaping so their delimiters are preserved
      var codeBlocks = []
      content = content.replace(/```(\w*)\n?([\s\S]*?)```/g, function (_, lang, code) {
        var placeholder = '\x00CODE' + codeBlocks.length + '\x00'
        var langClass = lang ? ' class="language-' + lang + '"' : ''
        codeBlocks.push('<pre class="cb-code-block"><code' + langClass + '>' + escapeHtml(code.trim()) + '</code></pre>')
        return placeholder
      })

      // Escape all remaining raw text before inserting any HTML
      content = escapeHtml(content)

      content = content
        .replace(/`([^`]+)`/g, function (_, code) {
          return '<code class="cb-inline-code">' + code + '</code>'
        })
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/^### (.+)$/gm, '<h6 class="cb-h">$1</h6>')
        .replace(/^## (.+)$/gm, '<h5 class="cb-h">$1</h5>')
        .replace(/^# (.+)$/gm, '<h4 class="cb-h">$1</h4>')
        .replace(/\n\n/g, '</p><p class="cb-p">')
        .replace(/\n/g, '<br>')

      // Restore code blocks
      content = content.replace(/\x00CODE(\d+)\x00/g, function (_, i) {
        return codeBlocks[parseInt(i)]
      })

      return '<p class="cb-p">' + content + '</p>'
    }
  },
  methods: {
    adaptedToolCall: function (tc) {
      return {
        name: tc.function && tc.function.name,
        type: tc.type || 'function',
        arguments: tc.function && tc.function.arguments,
        result: tc._result || null
      }
    }
  }
}

function escapeHtml (text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
</script>

<style scoped lang="scss">
.cb {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;

  &--user {
    flex-direction: row-reverse;

    .cb__body {
      align-items: flex-end;
    }

    .cb__content {
      background: linear-gradient(135deg, #6c80ff 0%, #764ba2 100%);
      color: #fff;
      border-radius: 18px 18px 4px 18px;
      box-shadow:
        0 3px 14px rgba(108, 128, 255, 0.3),
        0 1px 0 rgba(255, 255, 255, 0.12) inset;
    }
  }

  &--assistant {
    .cb__content {
      background: rgba(255, 255, 255, 0.04);
      color: rgba(255, 255, 255, 0.88);
      border-radius: 4px 18px 18px 18px;
      border: 1px solid rgba(255, 255, 255, 0.07);
      border-top-color: rgba(255, 255, 255, 0.12);
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
    }
  }
}

.cb__avatar {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: rgba(108, 128, 255, 0.12);
  border: 1px solid rgba(108, 128, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  box-shadow: 0 0 10px rgba(108, 128, 255, 0.15);

  i {
    font-size: 14px;
    background: linear-gradient(135deg, #6c80ff, #a078d0);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}

.cb__avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.cb__body {
  display: flex;
  flex-direction: column;
  max-width: 84%;
  min-width: 0;
}

.cb__agent-name {
  font-size: 11px;
  font-weight: 600;
  color: rgba(108, 128, 255, 0.75);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 4px;
  padding-left: 2px;
}

.cb__content {
  padding: 11px 16px;
  font-size: 14px;
  line-height: 1.65;
  overflow-wrap: break-word;

  :deep(.cb-p) {
    margin: 0 0 8px;
    &:last-child { margin-bottom: 0; }
  }

  :deep(.cb-h) {
    margin: 12px 0 6px;
    font-weight: 600;
    color: inherit;
    opacity: 0.95;
  }

  :deep(.cb-code-block) {
    background: #1a1d2e;
    border: 1px solid rgba(139, 92, 246, 0.25);
    border-radius: 8px;
    padding: 12px 14px;
    font-size: 13px;
    overflow-x: auto;
    margin: 10px 0;
    white-space: pre-wrap;
    font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', monospace;
    line-height: 1.5;
    color: #e2e8f0;
  }

  :deep(.cb-inline-code) {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12.5px;
    font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', monospace;
  }

  :deep(strong) {
    font-weight: 700;
    opacity: 0.95;
  }
}

.cb__images {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.cb__image {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid rgba(255,255,255,0.1);
}

.cb__meta {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 5px;
  padding: 0 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.2);

  i {
    font-size: 11px;
  }
}

.cb__error {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(220, 60, 60, 0.1);
  border: 1px solid rgba(220, 60, 60, 0.25);
  color: rgba(255, 120, 120, 0.9);
  font-size: 13px;

  i {
    font-size: 13px;
    flex-shrink: 0;
  }
}

.cb__thinking {
  border: 1px solid rgba(108, 128, 255, 0.18);
  border-radius: 10px;
  background: rgba(108, 128, 255, 0.04);
  margin-bottom: 8px;
  overflow: hidden;
}

.cb__thinking-header {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  padding: 7px 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(160, 170, 255, 0.75);
  font-size: 12px;
  text-align: left;
}

.cb__thinking-icon {
  font-size: 11px;
  opacity: 0.7;

  &--spin i {
    animation: thinking-pulse 1.4s ease-in-out infinite;
  }
}

@keyframes thinking-pulse {
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.15); }
}

.cb__thinking-label {
  flex: 1;
  font-style: italic;
}

.cb__thinking-chevron {
  font-size: 10px;
  opacity: 0.5;
}

.cb__thinking-body {
  padding: 0 12px 10px;
  border-top: 1px solid rgba(108, 128, 255, 0.1);
  margin-top: 0;
}

.cb__thinking-text {
  margin: 8px 0 0;
  font-size: 11.5px;
  line-height: 1.6;
  color: rgba(200, 210, 255, 0.5);
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
}
</style>
